import { 
    createSlice, 
    PayloadAction, 
    createEntityAdapter,
    EntityState,
} from "@reduxjs/toolkit";
import { RootState } from '../../../app/store';
import { fetchPortfolioAnalysis } from "../../../thunks/AnalysisStep";
import { PortfolioAnalysisStepResponse } from "../../../api/AnalysisStep";
import { CaseNote } from "../../../api/CaseNote";


export interface PortfolioAnalysisRecordInState {
    id: string; // Composite ID, e.g., "Last 7 Days-persona123"
    originalPersonaId: string;
    timeWindow: string;
    personaName: string;
    personaType: string;
    logins: number;
    rejections: number;
    sanctions: number;
    underReviews: number;
    delinquencyRate: number;
}

export interface PortfolioAnalysisDataState extends EntityState<PortfolioAnalysisRecordInState, string> {
    loadingStatus: 'idle' | 'pending' | 'succeeded' | 'failed';
    availableTimeWindows: string[];
    remarks?: CaseNote
}


const portfolioAnalysisAdapter = createEntityAdapter<PortfolioAnalysisRecordInState, string>({
    selectId: (record) => record.id,
});


const initialState: PortfolioAnalysisDataState = portfolioAnalysisAdapter.getInitialState({
    loadingStatus: 'idle',
    availableTimeWindows: [],
});

const portfolioAnalysisDataSlice = createSlice({
    name: 'portfolioAnalysisData',
    initialState,
    reducers: {
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchPortfolioAnalysis.pending, (state) => {
                state.loadingStatus = 'pending';
            })
            .addCase(fetchPortfolioAnalysis.fulfilled, (state, action: PayloadAction<PortfolioAnalysisStepResponse>) => {
                const allRecordsForAdapter: PortfolioAnalysisRecordInState[] = [];
                const timeWindowsFromPayload: string[] = [];

                action.payload.portfolio_details.forEach((detailsSet) => {
                    if(detailsSet.details.length > 0)
                        timeWindowsFromPayload.push(detailsSet.time_window)
                    detailsSet.details.forEach((details) => {
                        allRecordsForAdapter.push({
                            id: `${detailsSet.time_window}-${details.persona_id}`,
                            originalPersonaId: details.persona_id,
                            timeWindow: detailsSet.time_window,
                            personaName: details.persona_name,
                            personaType: details.persona_type,
                            logins: details.total_logins,
                            rejections: details.rejections,
                            sanctions: details.sanctioned,
                            underReviews: details.under_review,
                            delinquencyRate: details.delinquency,
                        })
                    })
                });
                portfolioAnalysisAdapter.setAll(state, allRecordsForAdapter); 
                state.availableTimeWindows = timeWindowsFromPayload;
                state.loadingStatus = 'succeeded';
                state.remarks = action.payload.remarks
            })
            .addCase(fetchPortfolioAnalysis.rejected, (state, action) => {
                state.loadingStatus = 'failed';
                portfolioAnalysisAdapter.removeAll(state); 
                state.availableTimeWindows = [];
            });
    }
});

export const portfolioAnalysisDataReducer = portfolioAnalysisDataSlice.reducer;

const portfolioAnalysisSelectors = portfolioAnalysisAdapter.getSelectors<RootState>(
    (state) => state.portfolioAnalysisData
);

export const selectAllPortfolioRecordsRaw = portfolioAnalysisSelectors.selectAll;
export const selectPortfolioAnalysisLoadingStatus = (state: RootState) => state.portfolioAnalysisData.loadingStatus;
export const selectAvailableTimeWindows = (state: RootState) => state.portfolioAnalysisData.availableTimeWindows;
export const selectPortfolioAnalysisRemarks = (state: RootState) => state.portfolioAnalysisData.remarks;
// Re: `selectPortfolioAnalysisRemarks`, see https://github.com/Yugen-ai/canso-fraud-investigation-ui/issues/89